import { z } from "zod";
import { idSchema, idempotencyKeySchema } from "./common.js";

/**
 * Phase 12d — input schemas for the write procedures. Every mutating
 * procedure embeds `idempotencyKey` via the `idempotencyEnvelopeSchema`
 * mixin (lifted off the input at the boundary by `withIdempotency`).
 *
 * The snapshot-save procedures (`sites.create/update/delete`,
 * `rooms.*`, `devices.*`, `panels.create/update/delete`) accept the
 * Phase 6a codec wire shape as `z.unknown()` at the contract boundary
 * — the per-aggregate JSON schema is re-validated inside the
 * procedure body via the existing `decodeSite(...)` / friends. The
 * split avoids two layers of zod parsing (contracts layer + codec
 * layer) for the same payload, and lets the codec own the
 * canonical wire-shape contract. The smart factory's invariants
 * (`R-SITE-1` non-empty name, room name uniqueness within a site,
 * device room-on-site, panel-in-building) re-fire on the codec's
 * `decodeXxx` path and surface as `BAD_REQUEST` through the
 * error-mapping middleware.
 *
 * Structural panel mutations (`applyPanelScan`, `linkSubPanel`,
 * `attachPhoto`, etc.) take narrower inputs — typically the resulting
 * `Panel` snapshot or the (subPanelId, parentCircuitId) tuple. The
 * snapshot-mutation arm names in `@panelgenie/domain` (`panel-scan`,
 * `link-sub-panel`, `photo-attach`) are the underlying domain model;
 * the procedure surface here flattens them into namespaced operations
 * the Phase 13 client calls by name.
 *
 * Aggregate ids are *client-generated* per the Phase 9f decision (so
 * concurrent creates from two offline clients do not collide on a
 * server-side counter); the schemas accept them verbatim from the
 * input and the smart factories re-validate non-emptiness.
 */

// --- common -------------------------------------------------------

/**
 * Every mutating procedure embeds an `idempotencyKey` field. The
 * `withIdempotency` helper reads it from `ctx.idempotencyKey` (lifted
 * by the Hono middleware ahead of the procedure body); the schema
 * here keeps the wire shape symmetric on the client side.
 */
const withIdempotencyEnvelope = <T extends z.ZodRawShape>(shape: T) =>
  z.object({ idempotencyKey: idempotencyKeySchema, ...shape });

// --- sites --------------------------------------------------------

export const siteCreateInputSchema = withIdempotencyEnvelope({
  site: z.unknown(),
});
export type SiteCreateInput = z.infer<typeof siteCreateInputSchema>;

export const siteUpdateInputSchema = withIdempotencyEnvelope({
  site: z.unknown(),
});
export type SiteUpdateInput = z.infer<typeof siteUpdateInputSchema>;

export const siteDeleteInputSchema = withIdempotencyEnvelope({
  id: idSchema,
});
export type SiteDeleteInput = z.infer<typeof siteDeleteInputSchema>;

// --- rooms --------------------------------------------------------

export const roomCreateInputSchema = withIdempotencyEnvelope({
  room: z.unknown(),
});
export type RoomCreateInput = z.infer<typeof roomCreateInputSchema>;

export const roomUpdateInputSchema = withIdempotencyEnvelope({
  room: z.unknown(),
});
export type RoomUpdateInput = z.infer<typeof roomUpdateInputSchema>;

export const roomDeleteInputSchema = withIdempotencyEnvelope({
  id: idSchema,
});
export type RoomDeleteInput = z.infer<typeof roomDeleteInputSchema>;

// --- devices ------------------------------------------------------

export const deviceCreateInputSchema = withIdempotencyEnvelope({
  device: z.unknown(),
});
export type DeviceCreateInput = z.infer<typeof deviceCreateInputSchema>;

export const deviceUpdateInputSchema = withIdempotencyEnvelope({
  device: z.unknown(),
});
export type DeviceUpdateInput = z.infer<typeof deviceUpdateInputSchema>;

export const deviceDeleteInputSchema = withIdempotencyEnvelope({
  id: idSchema,
});
export type DeviceDeleteInput = z.infer<typeof deviceDeleteInputSchema>;

// --- panels (snapshot save) ---------------------------------------

/**
 * `panels.create` carries the parent `siteId` + `buildingId` because a
 * panel's "where it lives" is on the site (`Site.buildings.panelIds`),
 * not on the panel value itself. The procedure threads the building
 * claim through the site aggregate at save time.
 */
export const panelCreateInputSchema = withIdempotencyEnvelope({
  siteId: idSchema,
  buildingId: idSchema,
  panel: z.unknown(),
});
export type PanelCreateInput = z.infer<typeof panelCreateInputSchema>;

export const panelUpdateInputSchema = withIdempotencyEnvelope({
  panel: z.unknown(),
});
export type PanelUpdateInput = z.infer<typeof panelUpdateInputSchema>;

export const panelDeleteInputSchema = withIdempotencyEnvelope({
  id: idSchema,
});
export type PanelDeleteInput = z.infer<typeof panelDeleteInputSchema>;

// --- panels (structural mutations) --------------------------------

/**
 * `panels.applyPanelScan` — the client ran Phase 8c's `applyPanelScan`
 * against its local panel + reviewed suggestion set, and ships the
 * resulting panel snapshot for the server to persist. Mirrors the
 * Phase 9f2 `panel-scan` snapshot-mutation arm; the server-side
 * `aiScansLifetime` ledger increments on a successful apply.
 */
export const panelsApplyPanelScanInputSchema = withIdempotencyEnvelope({
  panel: z.unknown(),
});
export type PanelsApplyPanelScanInput = z.infer<
  typeof panelsApplyPanelScanInputSchema
>;

/**
 * `panels.applyRoomDeviceScan` — additive batch of new devices, the
 * output of Phase 8d's `applyRoomDeviceScan`. Mirrors Phase 9f2's
 * `room-device-scan` arm. Carries the full device aggregates already
 * constructed via the Phase 4b smart factory client-side.
 */
export const panelsApplyRoomDeviceScanInputSchema = withIdempotencyEnvelope({
  siteId: idSchema,
  devices: z.array(z.unknown()),
});
export type PanelsApplyRoomDeviceScanInput = z.infer<
  typeof panelsApplyRoomDeviceScanInputSchema
>;

export const panelsLinkSubPanelInputSchema = withIdempotencyEnvelope({
  siteId: idSchema,
  subPanelId: idSchema,
  parentCircuitId: idSchema,
});
export type PanelsLinkSubPanelInput = z.infer<
  typeof panelsLinkSubPanelInputSchema
>;

export const panelsUnlinkSubPanelInputSchema = withIdempotencyEnvelope({
  siteId: idSchema,
  subPanelId: idSchema,
});
export type PanelsUnlinkSubPanelInput = z.infer<
  typeof panelsUnlinkSubPanelInputSchema
>;

/**
 * `photoAttachTargetSchema` mirrors the domain `PhotoAttachTarget`
 * sum type. Photos live on the site / device / circuit aggregates (not
 * directly on the panel — per the Phase 9f2 mutation model). The
 * `panels.*` namespace is the procedure surface ("attach a photo while
 * viewing this panel"); the actual aggregate target is in the body.
 */
export const photoAttachTargetSchema = z.discriminatedUnion("kind", [
  z.object({ kind: z.literal("site"), siteId: idSchema }),
  z.object({ kind: z.literal("device"), deviceId: idSchema }),
  z.object({ kind: z.literal("circuit"), circuitId: idSchema }),
]);
export type PhotoAttachTarget = z.infer<typeof photoAttachTargetSchema>;

export const panelsAttachPhotoInputSchema = withIdempotencyEnvelope({
  siteId: idSchema,
  target: photoAttachTargetSchema,
  photoId: idSchema,
});
export type PanelsAttachPhotoInput = z.infer<
  typeof panelsAttachPhotoInputSchema
>;

export const panelsDetachPhotoInputSchema = withIdempotencyEnvelope({
  siteId: idSchema,
  target: photoAttachTargetSchema,
  photoId: idSchema,
});
export type PanelsDetachPhotoInput = z.infer<
  typeof panelsDetachPhotoInputSchema
>;

export const panelsAcknowledgeSafetyInputSchema = withIdempotencyEnvelope({
  panelId: idSchema,
  acknowledgement: z.unknown(),
});
export type PanelsAcknowledgeSafetyInput = z.infer<
  typeof panelsAcknowledgeSafetyInputSchema
>;

// --- warnings -----------------------------------------------------

export const warningsDismissInputSchema = withIdempotencyEnvelope({
  panelId: idSchema,
  warningId: idSchema,
  note: z.string().optional(),
});
export type WarningsDismissInput = z.infer<typeof warningsDismissInputSchema>;

/**
 * `warnings.applySuggestedAction` — the server re-runs the typed
 * action against the offending entity (circuit / breaker), then
 * Phase 3a's reconciler removes the warning automatically when the
 * predicate no longer fires. The input carries the action verbatim
 * (the client read it off the warning's `suggestedAction` field); the
 * procedure does not synthesize a new action. The reconciler is run
 * server-side after the mutation so the warning auto-resolves before
 * the response is sent.
 */
export const warningsApplySuggestedActionInputSchema = withIdempotencyEnvelope({
  panelId: idSchema,
  warningId: idSchema,
  action: z.unknown(),
});
export type WarningsApplySuggestedActionInput = z.infer<
  typeof warningsApplySuggestedActionInputSchema
>;

// --- oplog --------------------------------------------------------

/**
 * `oplog.push` — batch of `OperationLogEntry` (Phase 9a's
 * `FieldUpdate` + Phase 9f1's `SnapshotMutation` arms) for one client
 * device, run through Phase 9b's reconciler against the server's site
 * graph. Returns the discard set so the client can surface the LWW
 * losers in its own UI.
 *
 * The wire shape is the Phase 9a `OperationLog` codec: `operations[]`
 * for field-level ops and `mutations[]` for structural mutations.
 */
export const oplogPushInputSchema = withIdempotencyEnvelope({
  siteId: idSchema,
  log: z.unknown(),
});
export type OplogPushInput = z.infer<typeof oplogPushInputSchema>;
